import { useState, useEffect } from 'react';
import { X, Loader2, Calendar, Clock, MapPin, FileText, Users, DollarSign } from 'lucide-react';
import { ZipAutoFillInput } from './ZipAutoFillInput';
import api from '../../../lib/api';
import { quoteJobService } from '../../../services/quoteJobService';
import toast from 'react-hot-toast';

export default function UpdateJobModal({ job, isOpen, onClose, onSuccess }) {
    const [surveyors, setSurveyors] = useState([]);
    const [loadingSurveyors, setLoadingSurveyors] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        start_date: '',
        start_time: '',
        end_time: '',
        location: '',
        zip_code: '',
        visit_instructions: '',
        notes: '',
        surveyors: []
    });

    useEffect(() => {
        if (isOpen) {
            // Load surveyors
            setLoadingSurveyors(true);
            quoteJobService.getSurveyors()
                .then((response) => {
                    if (response.data.success) {
                        setSurveyors(response.data.data || []);
                    }
                })
                .catch(() => {
                    console.log('Surveyors not available');
                })
                .finally(() => {
                    setLoadingSurveyors(false);
                });

            // Initialize form with job data
            const jobData = job?.raw || job || {};
            const quote = jobData.quote || {};
            const startDate = jobData.start_date ? jobData.start_date.split('T')[0] : '';
            const startTime = jobData.start_time ? jobData.start_time.split(':').slice(0, 2).join(':') : '';
            const endTime = jobData.end_time ? jobData.end_time.split(':').slice(0, 2).join(':') : '';

            // Convert surveyors from API format (with pivot.amount) to form format (with amount)
            const surveyors = (jobData.surveyors || []).map(surveyor => ({
                id: surveyor.id,
                amount: surveyor.pivot?.amount ? parseFloat(surveyor.pivot.amount) : (surveyor.amount || 0)
            }));

            setFormData({
                start_date: startDate,
                start_time: startTime,
                end_time: endTime,
                location: jobData.location || '',
                zip_code: quote.zip_code || jobData.zip_code || '',
                visit_instructions: jobData.visit_instructions || '',
                notes: jobData.notes || '',
                surveyors: surveyors
            });
        }
    }, [isOpen, job]);

    const handleSurveyorChange = (e) => {
        const surveyorId = parseInt(e.target.value);
        if (!surveyorId) {
            setFormData(prev => ({ ...prev, surveyors: [] }));
            return;
        }
        const existingSurveyor = formData.surveyors.find(s => s.id === surveyorId);
        if (!existingSurveyor) {
            setFormData(prev => ({
                ...prev,
                surveyors: [{ id: surveyorId, amount: 0 }]
            }));
        }
    };

    const handleUpdateSurveyorAmount = (amount) => {
        if (formData.surveyors.length === 0) return;
        setFormData(prev => ({
            ...prev,
            surveyors: prev.surveyors.map((s, idx) => 
                idx === 0 ? { ...s, amount: parseFloat(amount) || 0 } : s
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!job?.id) return;

        setIsSubmitting(true);
        try {
            const payload = {
                start_date: formData.start_date || null,
                start_time: formData.start_time || null,
                end_time: formData.end_time || null,
                location: formData.location || null,
                zip_code: formData.zip_code || null,
                visit_instructions: formData.visit_instructions || null,
                notes: formData.notes || null,
                surveyors: formData.surveyors.map(s => ({
                    id: s.id,
                    amount: s.amount || 0
                }))
            };

            const response = await api.put(`/company/jobs/${job.id}`, payload);
            
            if (response.data.success) {
                toast.success('Job updated successfully');
                onSuccess?.();
                onClose();
            } else {
                throw new Error(response.data.message || 'Failed to update job');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'Failed to update job');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const selectedSurveyorId = formData.surveyors.length > 0 ? formData.surveyors[0].id : '';
    const selectedSurveyorAmount = formData.surveyors.length > 0 ? formData.surveyors[0].amount : '';

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">Update Job</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-6">
                        {/* Schedule */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                                <Calendar className="w-4 h-4" />
                                Schedule
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5" />
                                        Start Date <span className="text-gray-400 font-normal">(Optional)</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                        <Clock className="w-3.5 h-3.5" />
                                        Start Time <span className="text-gray-400 font-normal">(Optional)</span>
                                    </label>
                                    <input
                                        type="time"
                                        value={formData.start_time}
                                        onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                        <Clock className="w-3.5 h-3.5" />
                                        End Time <span className="text-gray-400 font-normal">(Optional)</span>
                                    </label>
                                    <input
                                        type="time"
                                        value={formData.end_time}
                                        onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        {/* Location */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                                <MapPin className="w-4 h-4" />
                                Location
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Zip Code
                                    </label>
                                    <ZipAutoFillInput
                                        zip={formData.zip_code || ""}
                                        onZipChange={(zip) => setFormData(prev => ({ ...prev, zip_code: zip || "" }))}
                                        onAddressResolved={({ city, state, country }) => {
                                            const parts = [city, state, country].filter(Boolean);
                                            if (parts.length > 0) {
                                                setFormData(prev => ({ ...prev, location: parts.join(', ') }));
                                            }
                                        }}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Location
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Miami Marina"
                                        value={formData.location}
                                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        {/* Instructions & Notes */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                                <FileText className="w-4 h-4" />
                                Instructions & Notes
                            </h3>
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Visit Instructions
                                    </label>
                                    <textarea
                                        placeholder="Add instructions for the surveyor (e.g., Call upon arrival, Gate code, etc.)"
                                        value={formData.visit_instructions}
                                        onChange={(e) => setFormData(prev => ({ ...prev, visit_instructions: e.target.value }))}
                                        rows={3}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs resize-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Internal Notes
                                    </label>
                                    <textarea
                                        placeholder="Add any internal notes about this job..."
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        rows={3}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs resize-none"
                                    />
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        {/* Assign Surveyor */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                                <Users className="w-4 h-4" />
                                Assign Surveyor
                            </h3>

                            {loadingSurveyors ? (
                                <div className="p-4 text-center text-xs text-gray-500">Loading surveyors...</div>
                            ) : surveyors.length > 0 ? (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                                Select Surveyor
                                            </label>
                                            <div className="relative">
                                                <select
                                                    value={selectedSurveyorId}
                                                    onChange={handleSurveyorChange}
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs appearance-none bg-white"
                                                >
                                                    <option value="">Select Surveyor</option>
                                                    {surveyors.map(surveyor => (
                                                        <option key={surveyor.id} value={surveyor.id}>
                                                            {surveyor.full_name || surveyor.name || surveyor.user?.name || `Surveyor ${surveyor.id}`}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {selectedSurveyorId && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                                    Payment Amount ($)
                                                </label>
                                                <div className="relative">
                                                    <DollarSign className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                                                    <input
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={selectedSurveyorAmount}
                                                        onChange={(e) => handleUpdateSurveyorAmount(e.target.value)}
                                                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {selectedSurveyorId && (
                                        <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                            <h4 className="text-xs font-bold text-gray-900 mb-3 border-b border-gray-100 pb-2">Surveyor Details</h4>
                                            <div className="flex items-start gap-4">
                                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                                    {(() => {
                                                        const s = surveyors.find(sur => sur.id === selectedSurveyorId);
                                                        const name = s?.full_name || s?.name || s?.user?.name || "?";
                                                        return name.charAt(0).toUpperCase();
                                                    })()}
                                                </div>
                                                <div className="space-y-1">
                                                    {(() => {
                                                        const s = surveyors.find(sur => sur.id === selectedSurveyorId);
                                                        return (
                                                            <>
                                                                <p className="text-sm font-bold text-gray-900">
                                                                    {s?.full_name || s?.name || s?.user?.name || "Unknown Name"}
                                                                </p>
                                                                <div className="flex flex-col gap-1">
                                                                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                                                        <Users className="w-3 h-3" />
                                                                        {s?.email || s?.user?.email || "No email"}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                                                        <Users className="w-3 h-3" />
                                                                        {s?.phone || s?.user?.phone || "No phone"}
                                                                    </p>
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="p-4 text-center text-xs text-gray-500">No surveyors available</div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition flex items-center gap-2 disabled:opacity-70"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                'Update Job'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
